import axios from 'axios';

// withCredentials so the browser sends the HttpOnly session cookie the API now issues at sign-in.
// That cookie is the real credential for a browser session: script cannot read it, so an XSS can
// misbehave inside the page but cannot carry the session away to be used from somewhere else.
const api = axios.create({ baseURL: '/api', timeout: 120000, withCredentials: true });

// The CSRF half of the pair. The session cookie is HttpOnly and travels on its own; this one is
// deliberately readable, and echoing it back proves the caller can read our cookies — something
// a page on another origin cannot do. See backend/utils/sessionCookie.js.
const CSRF_COOKIE = 'rostery_csrf';

export function readCsrfToken() {
  const raw = document.cookie || '';
  for (const part of raw.split(';')) {
    const i = part.indexOf('=');
    if (i < 0) continue;
    if (part.slice(0, i).trim() === CSRF_COOKIE) {
      try { return decodeURIComponent(part.slice(i + 1).trim()); } catch { return part.slice(i + 1).trim(); }
    }
  }
  return null;
}

api.interceptors.request.use(cfg => {
  // The Bearer header stays for now. Sessions created before this shipped exist only in
  // localStorage, and dropping the header outright would sign every one of those people out at the
  // moment of deploy. It falls away on its own as sessions turn over.
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  // Writes need it; a few downloads that change something (a claim file download locks time) need
  // it too, so it goes on every request. It is our own cookie echoed back, so it leaks nothing.
  const csrf = readCsrfToken();
  if (csrf) cfg.headers['X-CSRF-Token'] = csrf;
  return cfg;
});

api.interceptors.response.use(
  r => r,
  async err => {
    // A download asks for responseType:'blob', so when it FAILS the JSON error body arrives as a
    // Blob too — and every `e.response.data.message` in the app then reads undefined. The operator
    // saw a generic "Failed to export" while the server was naming the exact lines that were wrong
    // (the claim file's 422 carries an `errors` array built for precisely that). Unwrap it once
    // here, so every download path in the app gets the real message instead of each having to
    // remember to do this.
    const raw = err.response?.data;
    if (raw instanceof Blob && (raw.type || '').includes('json')) {
      try { err.response.data = JSON.parse(await raw.text()); } catch { /* not JSON after all — leave the blob alone */ }
    }
    if (err.response?.status === 400 && process.env.NODE_ENV !== 'production') {
      console.error('[API 400]', err.config?.method?.toUpperCase(), err.config?.url, err.response?.data);
    }
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('rostery.activeBranch');
      delete api.defaults.headers.common['X-Branch-Id'];
      // Do not navigate if we are already on the login page. A 401 raised by something on
      // that page — anything loading org data before sign-in — would otherwise navigate to
      // /login, remount the caller, fire the same request and loop, leaving the page
      // reloading forever until the user pressed refresh.
      const path = window.location.pathname;
      if (path !== '/login' && !path.startsWith('/login/')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;
